import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Calendar, CalendarPlus, Download, MapPin, Phone, Video, RefreshCw, XCircle, CheckCircle2, UserX, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { interviewsV2, errorText, fmtIst } from './interviewApi';
import ScheduleInterviewDialog from './ScheduleInterviewDialog';
import { StatusPill, ModePill, OutcomeDialog, CancelDialog } from './InterviewActions';

/**
 * CandidateInterviewCard - the "Interviews" block on the candidate detail page, reading v2.
 * Self-contained: owns its dialogs, reloads itself. Legacy (pre-v2) rows are shown read-only.
 *
 * Props: candidate { id, name|full_name, email, phone }, requirementId?, onChanged?()
 */
export default function CandidateInterviewCard({ candidate, requirementId, onChanged }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [reschedule, setReschedule] = useState(null);
  const [outcomeFor, setOutcomeFor] = useState(null);
  const [outcomePreset, setOutcomePreset] = useState('pending');
  const [cancelFor, setCancelFor] = useState(null);

  const load = useCallback(async () => {
    if (!candidate?.id) return;
    try {
      setLoading(true);
      const r = await interviewsV2.forCandidate(candidate.id);
      setRows(r.interviews || []);
    } catch (e) {
      // never let a card fetch take the page down
      setRows([]);
    } finally { setLoading(false); }
  }, [candidate?.id]);

  useEffect(() => { load(); }, [load]);

  const changed = () => { load(); if (onChanged) onChanged(); };

  const resend = async (iv) => {
    try { const r = await interviewsV2.resendInvite(iv.id); toast.success(`Invite re-sent (${Object.values(r.email.results || {}).join(', ')})`); }
    catch (e) { toast.error(errorText(e.message)); }
  };

  const ModeIcon = ({ mode }) => (mode === 'in_person' ? <MapPin className="w-3.5 h-3.5" /> : mode === 'phone' ? <Phone className="w-3.5 h-3.5" /> : <Video className="w-3.5 h-3.5" />);
  const open = rows.filter((r) => r.status === 'scheduled');
  const past = rows.filter((r) => r.status !== 'scheduled');

  return (
    <Card className="mb-6" data-testid="candidate-interview-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Calendar className="w-5 h-5 text-emerald-600" /> Interviews {rows.length ? `(${open.length} open / ${rows.length})` : ''}
          </CardTitle>
          <Button size="sm" onClick={() => setScheduleOpen(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="schedule-interview-v2-btn">
            <CalendarPlus className="w-4 h-4 mr-1" /> Schedule interview
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading && <p className="text-sm text-slate-400">Loading...</p>}
        {!loading && rows.length === 0 && <p className="text-sm text-slate-500">No interviews yet. Schedule one - the candidate and the client contact get an email with a calendar invite, and reminders are handled for you.</p>}
        <div className="space-y-2">
          {[...open, ...past].map((iv) => (
            <div key={iv.id} className={`rounded-lg border p-3 ${iv.status === 'scheduled' ? 'bg-white border-slate-200' : 'bg-slate-50 border-slate-100'}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-slate-900">{iv.round_name || iv.title || 'Interview'}</span>
                    <StatusPill interview={iv} />
                    {iv.mode && <ModePill mode={iv.mode} />}
                    {!iv.is_v2 && <span className="text-[10px] uppercase tracking-wide text-slate-400">legacy</span>}
                  </div>
                  <div className="text-sm text-slate-600 mt-1 flex items-center gap-1">
                    <ModeIcon mode={iv.mode} /> {iv.when_ist || fmtIst(iv.scheduled_at || iv.start_time)}
                    {iv.duration_minutes ? ` - ${iv.duration_minutes} min` : ''}
                    {iv.client_name ? ` - ${iv.client_name}` : iv.company_name ? ` - ${iv.company_name}` : ''}
                    {iv.position_title ? ` (${iv.position_title})` : ''}
                  </div>
                  {(iv.client_contact_name || (iv.interviewer_names && iv.interviewer_names.length > 0)) && (
                    <div className="text-xs text-slate-500 mt-0.5">
                      {iv.client_contact_name ? `Client contact: ${iv.client_contact_name}` : ''}
                      {iv.client_contact_name && iv.interviewer_names?.length ? ' - ' : ''}
                      {iv.interviewer_names?.length ? `Interviewers: ${iv.interviewer_names.join(', ')}` : ''}
                    </div>
                  )}
                  {iv.mode === 'video' && iv.meeting_link && iv.status === 'scheduled' && (
                    <a href={iv.meeting_link} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline">Join link</a>
                  )}
                  {iv.outcome_notes && <p className="text-xs text-slate-600 mt-1 italic">"{iv.outcome_notes}"</p>}
                  {iv.cancel_reason && <p className="text-xs text-slate-500 mt-1">Cancelled: {iv.cancel_reason}</p>}
                  {iv.calendar_sync && iv.status === 'scheduled' && (
                    <p className="text-[11px] text-slate-400 mt-1">{iv.calendar_sync === 'google' ? 'Google Calendar + .ics sent' : '.ics invites sent'}{iv.reminder_count ? ` - ${iv.reminder_count} reminder(s) sent` : ''}</p>
                  )}
                </div>
                {iv.is_v2 && (
                  <div className="flex flex-wrap gap-1">
                    {iv.status === 'scheduled' && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => setReschedule(iv)} title="Reschedule"><RefreshCw className="w-3.5 h-3.5 mr-1" />Reschedule</Button>
                        <Button size="sm" variant="outline" onClick={() => { setOutcomePreset('pending'); setOutcomeFor(iv); }} title="Record outcome"><CheckCircle2 className="w-3.5 h-3.5 mr-1" />Outcome</Button>
                        <Button size="sm" variant="outline" onClick={() => { setOutcomePreset('no_show'); setOutcomeFor(iv); }} title="No-show"><UserX className="w-3.5 h-3.5" /></Button>
                        <Button size="sm" variant="outline" onClick={() => resend(iv)} title="Re-send invite"><Mail className="w-3.5 h-3.5" /></Button>
                        <Button size="sm" variant="outline" onClick={() => interviewsV2.downloadIcs(iv.id).catch((e) => toast.error(e.message))} title="Download .ics"><Download className="w-3.5 h-3.5" /></Button>
                        <Button size="sm" variant="ghost" className="text-red-600" onClick={() => setCancelFor(iv)} title="Cancel"><XCircle className="w-3.5 h-3.5" /></Button>
                      </>
                    )}
                    {iv.status === 'no_show' && (
                      <Button size="sm" variant="outline" onClick={() => setReschedule(iv)}><RefreshCw className="w-3.5 h-3.5 mr-1" />Reschedule</Button>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </CardContent>

      <ScheduleInterviewDialog open={scheduleOpen} onOpenChange={setScheduleOpen} candidate={candidate} requirementId={requirementId} onSaved={changed} />
      <ScheduleInterviewDialog open={!!reschedule} onOpenChange={(v) => { if (!v) setReschedule(null); }} candidate={candidate} interview={reschedule} onSaved={changed} />
      <OutcomeDialog open={!!outcomeFor} onOpenChange={(v) => { if (!v) setOutcomeFor(null); }} interview={outcomeFor} presetOutcome={outcomePreset} onDone={changed} />
      <CancelDialog open={!!cancelFor} onOpenChange={(v) => { if (!v) setCancelFor(null); }} interview={cancelFor} onDone={changed} />
    </Card>
  );
}
